import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import { Tag as TagComponent } from "components/DesignSystem/Tag/Tag";
import { Seperator } from "components/icons/Chat/Seperator";
import {
  TICKET_CREATE_END_DATE,
  TICKET_CREATE_SELECT_PERIOD,
  TICKET_CREATE_START_DATE,
  TICKET_ETD_END_DATE,
  TICKET_ETD_SELECT_PERIOD,
  TICKET_ETD_START_DATE,
  TICKET_PRIORITY_TYPES_DISPLAY,
  TICKET_PRIORITY_TYPES_TYPE,
  TICKET_TYPES,
  TICKET_RESOLVED_END_DATE,
  TICKET_RESOLVED_SELECT_PERIOD,
  TICKET_RESOLVED_START_DATE,
} from "constants/ticket";
import { useQuery } from "hooks/useQuery";
import ReactCountryFlag from "react-country-flag";
import { createColumnHelper } from "react-table-8.10.7";
import { ChatTicket } from "types/Models/chatTicket";
import { Tag } from "types/Models/FCA";
import { formatDate } from "utils/formatDate";
import { DateSelectHeader } from "./ColumnHeader/DateSelectHeader";
import { MultiselectHeader } from "./ColumnHeader/MultiselectHeader";
import { RadioHeader } from "./ColumnHeader/RadioHeader";
import { TicketActions } from "./TicketActions";
import { TicketETD } from "./TicketETD";
import { TicketNotes as TicketNotesComponent } from "./TicketNotes";
import { TicketPriority } from "./TicketPriority";
import { TicketTags } from "./TicketTags";
import { TicketAssigneeDropdown } from "./TicketAssigneeDropdown";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { useGetTicketTasksQuery } from "store/apis/chatTicket";
import { useGetServiceTeamQuery } from "store/apis/serviceTeam";
import { INKLE_TEAM_AND_CPA_TEAM } from "constants/chatType";

const priorityOptions = convertDisplayToOptions(TICKET_PRIORITY_TYPES_DISPLAY);

const columnHelper = createColumnHelper<ChatTicket>();

// Title column
const TitleHeader = () => {
  const queryData = useQuery();
  const selectedTicketType = queryData.get("ticket_type") || null;
  return (
    <RadioHeader
      options={TICKET_TYPES}
      selected={selectedTicketType || null}
      title="Ticket"
      filterKey="ticket_type"
    />
  );
};

const TitleCell = ({ row, table }: { row: any; table: any }) => {
  return (
    <div className="t-flex t-justify-items-center gap-2 t-h-full t-items-center">
      <div className="t-size-8 t-flex t-items-center t-justify-center t-rounded-full t-bg-neutral-0 t-flex-shrink-0">
        {table.options.meta.renderTicketTypeIcon(
          row.original?.is_priced_ticket
        )}
      </div>
      <div>
        <div className="t-flex t-flex-wrap t-items-center gap-2">
          {row.original.ticket_title}
          {table.options.meta.showInternalTicketBadge &&
            row.original?.is_internal_ticket && (
              <span className="t-flex-shrink-0">
                <TagComponent tagType="orange" size="small" icon={false}>
                  Internal
                </TagComponent>
              </span>
            )}
        </div>
        <div className="t-flex t-items-center gap-2">
          <div className="t-text-text-30">{row.original?.name}</div>
          <Seperator />
          {row.original?.entity && (
            <>
              <ReactCountryFlag
                style={{ width: "14px", height: "10px", marginBottom: "3px" }}
                countryCode={row.original?.entity.code_alpha_2}
                svg
              />
              <div className=" t-text-text-30">{row.original?.entity.name}</div>
              <Seperator />
            </>
          )}
          <div className="t-text-text-30">
            {row.original.company_group?.name}
          </div>
        </div>
      </div>
    </div>
  );
};

const TasksHeader = () => {
  const queryData = useQuery();
  const selectedTask = queryData.get("task_name") || null;
  const { data } = useGetTicketTasksQuery();

  return (
    <MultiselectHeader
      options={
        data?.map(({ name }) => ({
          value: name!,
          label: name!,
        })) || []
      }
      selected={selectedTask || null}
      title="Task"
      filterKey="task_name"
    />
  );
};

const TasksCell = ({ value }: { value: string | null }) => (
  <ConditionalToolTip condition={value && value}>
    <div className="t-text-body-sm t-text-text-60">{value || "-"}</div>
  </ConditionalToolTip>
);

const PriorityHeader = () => {
  const queryData = useQuery();
  const selectedPriority = queryData.get("priority") || null;

  return (
    <RadioHeader
      options={priorityOptions}
      selected={selectedPriority || null}
      title="priority"
      filterKey="priority"
    />
  );
};

const PriorityCell = ({
  value,
  ticketId,
}: {
  value: TICKET_PRIORITY_TYPES_TYPE | null;
  ticketId: string;
}) => <TicketPriority priority={value} ticketId={ticketId} />;

const SpocHeader = ({ callAgents }: { callAgents: any[] }) => {
  const queryData = useQuery();
  const selectedAgent = queryData.get("agent_ids") || null;

  const options = [
    {
      value: "unassigned",
      label: "Unassigned",
    },
    ...callAgents.map((agent) => ({
      value: agent.uuid,
      label: agent.name,
    })),
  ];

  return (
    <MultiselectHeader
      options={options}
      selected={selectedAgent || null}
      title="SPOC"
      filterKey="agent_ids"
    />
  );
};

const SpocCell = ({
  profileUrl,
  name,
}: {
  profileUrl?: string | null;
  name?: string;
}) => {
  return (
    <ConditionalToolTip condition={name && name}>
      <div
        className="t-min-w-14 t-text-center"
        onClick={(e) => e.stopPropagation()}
      >
        {name ? <Avatar alt={name} src={profileUrl || ""} /> : "-"}
      </div>
    </ConditionalToolTip>
  );
};

const AssigneeHeader = ({ assignees }: { assignees: any[] }) => {
  const queryData = useQuery();
  const selectedAssignee = queryData.get("assignee_profile_ids") || null;

  return (
    <MultiselectHeader
      options={assignees.map((assignee) => ({
        value: assignee.uuid,
        label: assignee.name,
      }))}
      selected={selectedAssignee || null}
      title="ASSIGNEES"
      filterKey="assignee_profile_ids"
    />
  );
};

const CreatedByHeader = () => {
  const queryData = useQuery();
  const selectedCreatedBy = queryData.get("created_by_profile_ids") || null;
  const { data: serviceTeamMembers = [] } = useGetServiceTeamQuery({
    accessible_teams: INKLE_TEAM_AND_CPA_TEAM,
  });

  return (
    <MultiselectHeader
      options={serviceTeamMembers.map((member) => ({
        value: member.uuid,
        label: member.name,
      }))}
      selected={selectedCreatedBy}
      title="CREATED BY"
      filterKey="created_by_profile_ids"
    />
  );
};

const CreatedOnHeader = () => (
  <DateSelectHeader
    startDateKey={TICKET_CREATE_START_DATE}
    endDateKey={TICKET_CREATE_END_DATE}
    selectPeriodKey={TICKET_CREATE_SELECT_PERIOD}
    title="CREATED ON"
  />
);

const CreatedOnCell = ({ value }: { value: string }) => (
  <div>{formatDate(value)}</div>
);

const ETDHeader = () => (
  <DateSelectHeader
    startDateKey={TICKET_ETD_START_DATE}
    endDateKey={TICKET_ETD_END_DATE}
    selectPeriodKey={TICKET_ETD_SELECT_PERIOD}
    title="ETD"
  />
);

const ETDCell = ({
  rowData,
  resolvedStatus,
}: {
  rowData: any;
  resolvedStatus: boolean;
}) => <TicketETD rowData={rowData} isResolved={resolvedStatus} />;

const TagsHeader = ({ tags }: { tags: any[] }) => {
  const queryData = useQuery();
  const selectedTag = queryData.get("tag_filter") || null;

  return (
    <MultiselectHeader
      options={tags.map((tag) => ({ value: tag.uuid, label: tag.title }))}
      selected={selectedTag || null}
      title="TAGS"
      filterKey="tag_filter"
    />
  );
};

const ResolvedAtCell = ({ value }: { value: string | null }) => (
  <div>
    <div>{value ? formatDate(value) : "-"}</div>
  </div>
);

const NotesCell = ({ rowData }: { rowData: any }) => (
  <TicketNotesComponent
    rowData={rowData}
    currentNoteLabel="Notes for this ticket"
    allNotesCount={rowData.notes_count || 0}
  />
);

const ActionsCell = ({
  uuid,
  is_escalated,
  resolved,
  is_internal_ticket,
}: {
  uuid: string;
  is_escalated: boolean;
  resolved: boolean;
  is_internal_ticket?: boolean;
}) => (
  <TicketActions
    ticketId={uuid}
    isEscalated={is_escalated}
    resolved={resolved}
    isInternalTicket={is_internal_ticket}
  />
);

export const ticketColumns = [
  columnHelper.accessor("ticket_title", {
    id: "title",
    header: TitleHeader,
    size: 20,
    cell: TitleCell,
  }),
  columnHelper.accessor("tasks", {
    id: "tasks",
    header: () => {
      return <TasksHeader />;
    },
    cell: (info) => <TasksCell value={info.getValue()} />,
    size: 15,
  }),
  columnHelper.accessor("priority", {
    id: "priority",
    header: () => <PriorityHeader />,
    size: 6,
    cell: ({ row }) => (
      <PriorityCell
        value={row.original.priority}
        ticketId={row.original.uuid}
      />
    ),
  }),
  columnHelper.accessor("company_group.assigned_agent.name", {
    cell: ({ row }) => (
      <SpocCell
        profileUrl={row.original.company_group?.assigned_agent?.profile_url}
        name={row.original.company_group?.assigned_agent?.name}
      />
    ),
    header: ({ table }) => {
      const meta = table.options.meta as { callAgents: string[] };

      return <SpocHeader callAgents={meta.callAgents} />;
    },
    size: 6,
  }),
  columnHelper.accessor("assignee", {
    id: "assignee",
    header: ({ table }) => {
      const meta = table.options.meta as { assignees: string[] };
      return <AssigneeHeader assignees={meta.assignees} />;
    },
    cell: (info) => (
      <TicketAssigneeDropdown
        currentTicketAssignees={info.row.original.assignee || []}
        ticketId={info.row.original.uuid}
      />
    ),
    size: 7,
  }),
  columnHelper.accessor("assigned_by", {
    id: "created_by",
    header: CreatedByHeader,
    cell: ({ getValue }) => (
      <div className="t-text-body-sm t-text-text-60">{getValue() || "-"}</div>
    ),
    size: 8,
  }),
  columnHelper.accessor("created_on", {
    id: "created_on",
    header: CreatedOnHeader,
    cell: ({ getValue }) => <CreatedOnCell value={getValue()} />,
    size: 8,
  }),
  columnHelper.accessor("etd_date", {
    id: "etd_date",
    header: ETDHeader,
    cell: ({ row, table }) => {
      const meta = table.options.meta as { resolvedStatus: boolean };
      return (
        <ETDCell rowData={row.original} resolvedStatus={meta.resolvedStatus} />
      );
    },
    size: 7,
  }),
  columnHelper.accessor("tags", {
    id: "tags",
    header: ({ table }) => {
      const meta = table.options.meta as { tags: Tag[] };
      return <TagsHeader tags={meta.tags} />;
    },
    cell: TicketTags,
    size: 10,
  }),
  columnHelper.accessor("resolved_at", {
    id: "resolved_at",
    header: () => (
      <DateSelectHeader
        startDateKey={TICKET_RESOLVED_START_DATE}
        endDateKey={TICKET_RESOLVED_END_DATE}
        selectPeriodKey={TICKET_RESOLVED_SELECT_PERIOD}
        title="RESOLVED ON"
      />
    ),
    cell: ({ getValue }) => <ResolvedAtCell value={getValue()} />,
    size: 10,
  }),
  columnHelper.display({
    id: "notes",
    size: 3,
    cell: ({ row }) => <NotesCell rowData={row.original} />,
  }),
  columnHelper.display({
    id: "threedots",
    size: 3,
    cell: ({ row }) => (
      <ActionsCell
        uuid={row.original.uuid}
        is_escalated={row.original.is_escalated}
        resolved={row.original.resolved}
        is_internal_ticket={row.original.is_internal_ticket}
      />
    ),
  }),
];
